import type { LeadFormStepSchema } from "@shared/leadFormRules";
import type { LeadFormSkinSettingGroup, LeadFormSkinValues } from "./types";

/**
 * PER-STEP overrides for the copy and the CTA.
 *
 * ─── Why these are not skin settings ────────────────────────────────────────────────
 * A skin's settings are fixed: a developer writes them, and every form using that skin is
 * offered the same list. Steps are the opposite — they belong to the FORM, an author adds
 * and removes them in the Form Builder, and their ids are author-chosen. So the controls
 * for "step 2's button label" cannot exist until you know which form you are editing.
 *
 * These groups are therefore generated from the schema at edit time and appended to the
 * skin's own, which is why `splitSkinValues` takes the combined list rather than reading
 * the skin alone: a key nobody declared is a key that silently vanishes on save.
 *
 * ─── Why the key carries the step ID and not its index ──────────────────────────────
 * Indexes renumber. An author who inserts a screen before step 2 would otherwise find the
 * old step 2's button label attached to the new one, and every subsequent step's copy
 * shifted by one — silently, and only visible to a visitor. The id is stable for the life
 * of the step, so reordering moves the copy WITH the step and deleting it strands a key
 * that is simply never read again.
 */

/** The overridable properties of a single step. `store` decides which column holds it. */
export const STEP_SETTING_FIELDS = [
  { suffix: "title", label: "Title", type: "text", store: "copy" },
  { suffix: "subtitle", label: "Sub text", type: "textarea", store: "copy" },
  { suffix: "button_text", label: "Button label", type: "text", store: "copy" },
  { suffix: "button_color", label: "Button colour", type: "color", store: "theme" },
  { suffix: "button_text_color", label: "Button text colour", type: "color", store: "theme" },
] as const;

/** `step_<id>_<suffix>` — see the note above on why this is keyed by id. */
export const stepSettingKey = (stepId: string, suffix: string) =>
  `step_${String(stepId).trim()}_${suffix}`;

/** How a step is named in the settings panel, matching the Form Builder's own labels. */
const stepLabel = (step: LeadFormStepSchema, index: number): string => {
  const name = String(step?.title ?? "").trim();
  const kind = step?.kind === "otp" ? "OTP" : `Step ${index + 1}`;
  return name ? `${kind} — ${name}` : kind;
};

/**
 * One settings group per step, in journey order.
 *
 * Every value defaults to EMPTY rather than to the form-level value. An override has to be
 * distinguishable from "not overridden": seeding it with the global default would make
 * every step look customised, and changing the form-level title would then stop affecting
 * any step — the author would have to retype it once per screen.
 */
export const buildStepSettingGroups = (
  steps: LeadFormStepSchema[],
): LeadFormSkinSettingGroup[] =>
  (Array.isArray(steps) ? steps : [])
    .filter((step) => step && typeof step.id === "string" && step.id.trim() !== "")
    .map((step, index) => ({
      title: stepLabel(step, index),
      settings: STEP_SETTING_FIELDS.map((field, position) => ({
        key: stepSettingKey(step.id, field.suffix),
        label: field.label,
        type: field.type,
        store: field.store,
        defaultValue: "",
        help:
          position === 0
            ? "Left blank this step uses the form-level setting."
            : undefined,
      })),
    }));

export interface StepOverrides {
  title?: string;
  subtitle?: string;
  buttonText?: string;
  buttonColor?: string;
  buttonTextColor?: string;
}

/** Trimmed, with blanks dropped, so `??`/`||` at the call site means "not overridden". */
export const resolveStepOverrides = (
  values: LeadFormSkinValues | Record<string, unknown> | null | undefined,
  step: LeadFormStepSchema | null | undefined,
): StepOverrides => {
  const id = String(step?.id ?? "").trim();
  if (!id || !values) return {};
  const bag = values as Record<string, unknown>;
  const read = (suffix: string) => {
    const raw = bag[stepSettingKey(id, suffix)];
    const text = raw === undefined || raw === null ? "" : String(raw).trim();
    return text === "" ? undefined : text;
  };
  return {
    title: read("title"),
    subtitle: read("subtitle"),
    buttonText: read("button_text"),
    buttonColor: read("button_color"),
    buttonTextColor: read("button_text_color"),
  };
};
